import React, { useState } from "react"
import toast, { Toaster } from "react-hot-toast"

import { trpc } from "../lib/trpc"

interface CalendarEvent {
  summary: string
  startTime: string
}

// Format ICS date string (YYYYMMDDTHHMMSS) to readable format
const formatICSDate = (icsDate: string): string => {
  // Handle different ICS date formats
  // Basic format: YYYYMMDDTHHMMSS or YYYYMMDDTHHMMSSZ
  // With timezone: TZID=America/New_York:YYYYMMDDTHHMMSS

  // Remove timezone prefix if present
  const dateStr = icsDate.includes(":") ? icsDate.split(":")[1] : icsDate

  // Remove Z suffix if present
  const cleanDateStr = dateStr.replace("Z", "")

  // Parse the date components
  if (cleanDateStr.length >= 15) {
    const year = cleanDateStr.substring(0, 4)
    const month = cleanDateStr.substring(4, 6)
    const day = cleanDateStr.substring(6, 8)
    const hour = cleanDateStr.substring(9, 11)
    const minute = cleanDateStr.substring(11, 13)

    // Create a date object
    const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:00`)

    // Format the date nicely
    const options: Intl.DateTimeFormatOptions = {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }

    return date.toLocaleDateString("en-US", options)
  }

  // If we can't parse it, return the original
  return icsDate
}

/**
 * Calendar Parser - Parse ICS calendar URLs and extract events
 *
 * Features:
 * - Input URL for ICS calendar feed
 * - Parse and display events with summary and start time
 * - Copy results to clipboard
 * - Download as CSV
 */
function CalendarParser() {
  const [calendarUrl, setCalendarUrl] = useState("")
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const defaultUrl =
    "https://outlook.office365.com/owa/calendar/a1b189db932a461aad5af4a8544ec5c0@onlocationexp.com/2d499b7b7d344eb987c5e6066914efe61631742555388124211/S-1-8-3782327581-2927567538-2909484128-4157659993/reachcalendar.ics"

  const parseCalendarMutation = trpc.tools.parseCalendarUrl.useMutation({
    onSuccess: (data) => {
      setEvents(data.events)
      toast.success(`Successfully parsed ${data.events.length} events`)
    },
    onError: (error) => {
      toast.error(error.message || "Failed to parse calendar")
    },
  })

  const handleParseCalendar = async () => {
    if (!calendarUrl.trim()) {
      toast.error("Please enter a calendar URL")
      return
    }

    setIsLoading(true)
    try {
      await parseCalendarMutation.mutateAsync({ url: calendarUrl })
    } finally {
      setIsLoading(false)
    }
  }

  const copyToClipboard = () => {
    if (events.length === 0) {
      toast.error("No events to copy")
      return
    }

    const text = events.map((event) => `${event.summary}\t${formatICSDate(event.startTime)}`).join("\n")

    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success("Copied to clipboard!")
      })
      .catch(() => {
        toast.error("Failed to copy to clipboard")
      })
  }

  const downloadAsCSV = () => {
    if (events.length === 0) {
      toast.error("No events to download")
      return
    }

    const csvContent =
      "Summary,Start Time\n" +
      events.map((event) => `"${event.summary}","${formatICSDate(event.startTime)}"`).join("\n")

    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "calendar-events.csv"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    toast.success("CSV downloaded!")
  }

  const clearResults = () => {
    setEvents([])
    setCalendarUrl("")
    toast.success("Cleared results")
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#363636",
            color: "#fff",
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: "#4ade80",
              secondary: "#fff",
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: "#ef4444",
              secondary: "#fff",
            },
          },
        }}
      />

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold  mb-2">📅 Calendar Parser</h1>
        <p className="">Parse ICS calendar feeds and extract event information. Enter a calendar URL to get started.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Input Panel */}
        <div className="lg:col-span-1">
          <div className="bg-background rounded-lg border p-6 space-y-6">
            <h2 className="text-xl font-semibold ">🔗 Calendar URL</h2>

            {/* URL Input */}
            <div>
              <label className="block text-sm font-medium text-primary mb-2">ICS Calendar URL</label>
              <textarea
                value={calendarUrl}
                onChange={(e) => setCalendarUrl(e.target.value)}
                placeholder="https://outlook.office365.com/owa/calendar/..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 resize-none"
                disabled={isLoading}
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter the full URL to an ICS calendar feed (e.g., from Outlook, Google Calendar, etc.)
              </p>

              {/* Use Example URL Button */}
              <button
                onClick={() => setCalendarUrl(defaultUrl)}
                className="mt-2 w-full bg-foregroundx text-primary py-2 px-3 rounded-md  text-sm font-medium"
                disabled={isLoading}
              >
                📋 Use Example Calendar URL
              </button>
            </div>

            {/* Parse Button */}
            <button
              onClick={handleParseCalendar}
              disabled={isLoading || !calendarUrl.trim()}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isLoading ? "⏳ Parsing..." : "🔍 Parse Calendar"}
            </button>

            {/* Results Summary */}
            {events.length > 0 && (
              <div className="bg-foregroundx rounded-lg p-4">
                <h3 className="font-medium  mb-2">📊 Results</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Total Events:</span>
                    <span className="font-medium">{events.length}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {events.length > 0 && (
              <div className="space-y-3">
                <button
                  onClick={copyToClipboard}
                  className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 font-medium"
                >
                  📋 Copy to Clipboard
                </button>

                <button
                  onClick={downloadAsCSV}
                  className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg hover:bg-indigo-700 font-medium"
                >
                  💾 Download as CSV
                </button>

                <button
                  onClick={clearResults}
                  className="w-full bg-gray-600 text-white py-3 px-4 rounded-lg hover:bg-gray-700 font-medium"
                >
                  🗑️ Clear Results
                </button>
              </div>
            )}

            {/* Info Box */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2">ℹ️ About</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Supports standard ICS/iCal calendar formats</li>
                <li>• Extracts event summary and start time</li>
                <li>• All processing happens server-side</li>
                <li>• No data is stored - everything is processed in real-time</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Results Panel */}
        <div className="lg:col-span-2">
          <div className="bg-background rounded-lg border p-6">
            <h2 className="text-xl font-semibold  mb-4">📋 Events</h2>

            {events.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <div className="mb-4">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <p>No events parsed yet</p>
                <p className="text-sm">Enter a calendar URL and click Parse to see events</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-foreground">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Summary
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Start Time
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-background divide-y divide-gray-200">
                    {events.map((event, index) => (
                      <tr key={index} className="">
                        <td className="px-6 py-4 text-sm ">{event.summary}</td>
                        <td className="px-6 py-4 text-sm  whitespace-nowrap">{formatICSDate(event.startTime)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Tips */}
          {events.length > 0 && (
            <div className="mt-6 bg-green-50 rounded-lg p-4">
              <h3 className="font-medium text-green-900 mb-2">💡 Tips</h3>
              <ul className="text-sm text-green-800 space-y-1">
                <li>• Copy to clipboard preserves tab-separated format for easy pasting into spreadsheets</li>
                <li>• CSV download includes proper formatting for Excel/Google Sheets</li>
                <li>• Times are shown as they appear in the calendar feed</li>
                <li>• For recurring events, each occurrence is listed separately</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CalendarParser
